import React, {
    useEffect,
    useState,
    useContext,
    useMemo,
    memo,
    useRef,
    lazy,
    Suspense
} from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Corrected import paths based on typical project structure (assuming ArticlePage is deep)
import allArticlesData from '../data/jsons/articlesData.json'; // Assumed root is 2 levels up
import { ThemeContext } from '../context/ThemeContext';
import { findSimilarArticles } from '../utils/similarity'; 

// Lazy loading components
const ReactMarkdown = lazy(() => import('react-markdown'));
const HorizontalCardDeck = lazy(() => import('../widgets/HorizontalCardDeck')); 
const ArticleCard = lazy(() => import('../widgets/ArticleCard')); 

// --- Similar Articles Sub-Component ---

const SimilarArticles = memo(({ currentArticle, darkMode }) => {
    const [similarArticles, setSimilarArticles] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef(null);

    // --- Intersection Observer (One-Time Trigger) ---
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { root: null, rootMargin: '0px', threshold: 0.1 }
        );
        const ref = containerRef.current;
        if (ref) observer.observe(ref);
        return () => { if (ref) observer.unobserve(ref); };
    }, []);

    // --- Data Fetching (Triggered by visibility) ---
    useEffect(() => {
        if (!isVisible || similarArticles.length > 0) return;
        
        const timeoutId = setTimeout(() => {
            const results = findSimilarArticles(currentArticle.path, allArticlesData, 5);
            setSimilarArticles(results);
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [currentArticle, isVisible, similarArticles.length]);
    
    // --- Renderer for HorizontalCardDeck ---
    const renderSimilarArticleItem = (article) => (
        <ArticleCard 
            article={article} 
        />
    );

    // --- Loading States ---
    if (!isVisible && similarArticles.length === 0) {
        return <div ref={containerRef} style={{ minHeight: '150px' }} />; // Anchor placeholder
    }

    if (similarArticles.length === 0) {
        return (
            <div className="text-center my-5" ref={containerRef}>
                <div className={`spinner-border ${darkMode ? 'text-secondary' : 'text-primary'} w-10 h-10 border-4 border-t-transparent animate-spin rounded-full`} role="status">
                    <span className="sr-only">Finding Similar Articles...</span>
                </div>
                <p className={`mt-2 ${darkMode ? 'text-muted-dark' : 'text-muted-light'}`}>Searching related content...</p>
            </div>
        );
    }

    // --- Final Render with HorizontalCardDeck ---
    return (
        <div className="mt-8 pt-5 border-t border-border-light dark:border-border-dark" ref={containerRef}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                 <h3 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-text-dark' : 'text-text-light'}`}>
                    You Might Also Like
                </h3>
                <Suspense fallback={<div className="text-center py-3">Loading similar articles carousel...</div>}>
                    {/* The HorizontalCardDeck itself will attempt to use the full width of its container */}
                    <HorizontalCardDeck
                        items={similarArticles}
                        renderItem={renderSimilarArticleItem}
                        rows={1} 
                    />
                </Suspense>
            </div>
        </div>
    );
});

// --- Article Page Main Component ---

const ArticlePage = () => {
    const { darkMode } = useContext(ThemeContext);
    const { slug } = useParams();

    const article = useMemo(() => {
        if (!slug) return null;
        const cleanSlug = slug.endsWith('.md') ? slug.slice(0, -3) : slug;
        return allArticlesData.find(a => a.path === cleanSlug || a.path === slug);
    }, [slug]);

    const [markdown, setMarkdown] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const readingTime = useMemo(() => {
        const wordCount = markdown.trim() ? markdown.trim().split(/\s+/).length : 0;
        return Math.max(1, Math.ceil(wordCount / 220));
    }, [markdown]);

    useEffect(() => {
        if (article) {
            window.scrollTo({ top: 0, behavior: 'instant' });
            setIsLoading(true);
            const normalizedPath = article.path.endsWith('.md')
                ? article.path
                : `${article.path}.md`;

            // Fetch markdown content
            import(`../data/articles/${normalizedPath}`) 
                .then(module => fetch(module.default))
                .then(res => {
                    const contentType = res.headers.get('content-type') || '';
                    if (!res.ok || contentType.includes('text/html')) {
                        throw new Error(`Markdown request failed (${res.status}, ${contentType || 'unknown type'})`);
                    }
                    return res.text();
                })
                .then(text => {
                    if (!text.trim()) {
                        throw new Error('Markdown file is empty');
                    }
                    setMarkdown(text);
                    setIsLoading(false);
                })
                .catch(error => {
                    console.error("Error loading markdown:", error);
                    setMarkdown('Sorry, the article could not be loaded.');
                    setIsLoading(false);
                });
        }
    }, [article]);

    if (!article) return <Navigate to="/" replace />;

    // --- Tailwind/CSS Classes and Styles ---
    // FIX 2: Explicitly set the background for dark/light mode to ensure consistency across the viewport.
    const containerBgClass = darkMode ? 'bg-bg-dark' : 'bg-bg-light'; 
    // This container wraps the entire page, setting the main background color.
    // We remove the inner horizontal padding here since we apply it to the centered content block.
    const fullWidthContainerClass = `${containerBgClass} pt-5 pb-16`; 
    
    const dateClass = darkMode ? 'text-muted-dark' : 'text-muted-light';
    const markdownClass = `markdown-body prose dark:prose-invert max-w-none ${darkMode ? 'text-text-dark' : 'text-text-light'}`;
    
    // Max width constraint for the main article content text
    const contentStyle = { maxWidth: '880px' };

    return (
        <>
            <Helmet>
                <title>{`${article.title} | Article by Varun Rao`}</title>
                <meta name="description" content={article.description || article.excerpt} />
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={article.description || article.excerpt} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={window.location.href} />
                {article.imageUrl && <meta property="og:image" content={article.imageUrl} />}
                <link rel="canonical" href={window.location.href} />
            </Helmet>

            <div className={fullWidthContainerClass}>
                {/* Main Article Content Container - Centered and width-limited */}
                <div className="flex justify-center">
                    {/* The max-width is applied here, but we also reintroduce padding/margins for small screens */}
                    <div style={contentStyle} className="article-reading-shell w-full mx-3 sm:mx-6 px-4 py-5 sm:px-8 sm:py-8 lg:px-12">
                        <div className="mb-8 text-center">
                            {/* Image */}
                            <img
                                src={article.imageUrl || '/images/default-article.webp'}
                                alt={article.title}
                                className="article-hero-image w-full aspect-video object-cover rounded-xl shadow-xl mx-auto"
                                loading="eager"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/images/default-article.webp';
                                }}
                            />
                            <h1 className="article-title mt-6 font-extrabold text-text-light dark:text-text-dark">{article.title}</h1>
                            <div className={`article-meta ${dateClass} mt-4`}>
                                <span>Published {new Date(article.date).toLocaleDateString()}</span>
                                {!isLoading && <span aria-hidden="true">•</span>}
                                {!isLoading && <span>{readingTime} min read</span>}
                            </div>
                        </div>

                        {/* Article Content Area */}
                        {isLoading ? (
                            <div className="text-center py-10">
                                <div className="spinner-border w-10 h-10 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full" role="status">
                                    <span className="sr-only">Loading content...</span>
                                </div>
                            </div>
                        ) : (
                            <Suspense fallback={<div className="text-center py-5">Rendering article...</div>}>
                                <article className={markdownClass}>
                                    <ReactMarkdown>{markdown}</ReactMarkdown>
                                </article>
                            </Suspense>
                        )}
                    </div>
                </div>

                {/* Similar Articles Section 
                    FIX 1: Moved outside the max-width div to use the full available screen width 
                    (which is constrained only by the padding handled inside SimilarArticles).
                */}
                {!isLoading && article.embedding && (
                    <SimilarArticles
                        currentArticle={article}
                        darkMode={darkMode}
                    />
                )}
            </div>
        </>
    );
};

export default memo(ArticlePage);
